import React, { useEffect, useState } from "react";
import { Beef, Wheat, Droplet, Trophy, Dumbbell, Zap, Heart, Wind, ShieldCheck, Activity, Sprout, Salad, Apple, ShoppingCart, Download, Share2, Printer } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { buildShoppingPdf, buildShoppingText } from "../../lib/shoppingList";

const DAY_META = {
  partido:           { label: "Día de partido",     icon: Trophy,     color: "#E11D2E" },
  partido_suplente:  { label: "Partido suplente",   icon: ShieldCheck,color: "#F97316" },
  entreno_equipo:    { label: "Entreno con equipo", icon: Activity,   color: "#F5C518" },
  fuerza:            { label: "Día de fuerza",      icon: Dumbbell,   color: "#3B82F6" },
  explosividad:      { label: "Día de explosividad",icon: Zap,        color: "#A855F7" },
  recuperacion:      { label: "Día de recuperación",icon: Heart,      color: "#22C55E" },
  movilidad:         { label: "Día de movilidad",   icon: Wind,       color: "#22D3EE" },
};

const MACROS = [
  { key: "proteins_foods",   label: "PROTEÍNAS",     icon: Beef,    color: "#E11D2E", desc: "Construye y repara músculo." },
  { key: "carbs_foods",      label: "CARBOHIDRATOS", icon: Wheat,   color: "#EAB308", desc: "Tu combustible principal." },
  { key: "fats_foods",       label: "GRASAS",        icon: Droplet, color: "#22D3EE", desc: "Hormonas y energía sostenida." },
  { key: "legumes_foods",    label: "LEGUMBRES",     icon: Sprout,  color: "#84CC16", desc: "Proteína vegetal + fibra." },
  { key: "vegetables_foods", label: "VERDURAS",      icon: Salad,   color: "#22C55E", desc: "Micronutrientes y digestión." },
  { key: "fruits_foods",     label: "FRUTAS",        icon: Apple,   color: "#F97316", desc: "Vitaminas y energía rápida." },
];

export default function ClientNutrition() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/me/nutrition"); setPlan(data); })(); }, []);

  if (!plan) return <AppShell><div style={{ color: "#737373" }}>Cargando...</div></AppShell>;

  const totalFoods = MACROS.reduce((n, m) => n + ((plan[m.key] || []).length), 0);

  const downloadPdf = () => {
    try {
      const doc = buildShoppingPdf(plan, user?.name || "");
      doc.save(`Lista_Compra_QV_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Lista descargada");
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(buildShoppingText(plan));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const printList = () => {
    try {
      const doc = buildShoppingPdf(plan, user?.name || "");
      doc.autoPrint();
      const url = doc.output("bloburl");
      window.open(url, "_blank");
    } catch { toast.error("No se pudo imprimir"); }
  };

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="client-nutrition">
        <div className="label">Nutrición</div>
        <h1>Tu plan alimenticio</h1>
        <p className="sub">Alimentos recomendados por macronutriente y distribución de calorías según el tipo de día.</p>
      </div>

      {/* Shopping list CTA */}
      <div
        className="qv-card-app"
        style={{ marginBottom: 22, background: "linear-gradient(135deg, rgba(225,29,46,0.08) 0%, rgba(10,10,10,0.7) 100%)", borderColor: "rgba(225,29,46,0.35)" }}
        data-testid="shopping-list-card"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ width: 48, height: 48, background: "rgba(225,29,46,0.15)", border: "1px solid rgba(225,29,46,0.5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ShoppingCart size={22} color="#E11D2E" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="qv-overline">Compra de la semana</div>
            <div style={{ fontFamily: "Bebas Neue", fontSize: "1.5rem", marginTop: 2 }}>Tu lista lista en 1 click</div>
            <div style={{ color: "#a3a3a3", fontSize: 12, marginTop: 4 }}>
              {totalFoods} alimentos en {MACROS.filter((m) => (plan[m.key] || []).length).length} categorías. Descarga PDF, comparte por WhatsApp o imprime.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={downloadPdf} className="qv-btn-primary" style={{ fontSize: 11 }} data-testid="shopping-pdf-btn">
              <Download size={14} /> PDF
            </button>
            <button onClick={shareWhatsApp} className="qv-btn-ghost" style={{ fontSize: 11, borderColor: "rgba(37,211,102,0.4)", color: "#25D366" }} data-testid="shopping-whatsapp-btn">
              <Share2 size={14} /> WHATSAPP
            </button>
            <button onClick={printList} className="qv-btn-ghost" style={{ fontSize: 11 }} data-testid="shopping-print-btn">
              <Printer size={14} /> IMPRIMIR
            </button>
          </div>
        </div>
      </div>

      {/* Macro food cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 22 }}>
        {MACROS.map((m) => {
          const items = plan[m.key] || [];
          return (
            <div key={m.key} className="qv-card-app" style={{ borderColor: `${m.color}33` }} data-testid={`macro-${m.key}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 36, height: 36, background: `${m.color}18`, border: `1px solid ${m.color}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <m.icon size={18} color={m.color} />
                </div>
                <div>
                  <div style={{ fontFamily: "Bebas Neue", fontSize: "1.3rem", letterSpacing: "0.04em", color: "#fff" }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: "#737373" }}>{m.desc}</div>
                </div>
              </div>
              <div style={{ height: 1, background: `${m.color}22`, margin: "12px 0 14px" }} />
              {items.length === 0 ? (
                <div style={{ color: "#737373", fontSize: 13 }}>Tu coach aún no ha definido alimentos en esta categoría.</div>
              ) : (
                <div className="qv-tag-list">
                  {items.map((f, i) => (
                    <span key={i} className="qv-tag" style={{ fontSize: 13, borderColor: `${m.color}55`, color: "#fff" }}>{f}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Day cards */}
      <div className="qv-overline" style={{ marginBottom: 10 }}>Distribución por tipo de día</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginBottom: 22 }}>
        {plan.day_distributions.map((d) => {
          const meta = DAY_META[d.day_type] || { label: d.day_type, icon: Activity, color: "#a3a3a3" };
          const totalGrams = (d.protein_g || 0) + (d.carbs_g || 0) + (d.fat_g || 0);
          const pP = totalGrams ? Math.round(((d.protein_g || 0) / totalGrams) * 100) : 0;
          const pC = totalGrams ? Math.round(((d.carbs_g || 0) / totalGrams) * 100) : 0;
          const pF = totalGrams ? 100 - pP - pC : 0;
          return (
            <div key={d.day_type} className="qv-card-app" data-testid={`day-${d.day_type}`} style={{ background: "#0a0a0a", borderColor: `${meta.color}33` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, background: `${meta.color}18`, border: `1px solid ${meta.color}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <meta.icon size={15} color={meta.color} />
                  </div>
                  <div style={{ fontFamily: "Bebas Neue", fontSize: "1.15rem", color: "#fff", letterSpacing: "0.04em" }}>{meta.label}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "Bebas Neue", fontSize: "1.7rem", color: "var(--qv-red)", lineHeight: 1 }}>{d.kcal || "—"}</div>
                  <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.2em", textTransform: "uppercase" }}>kcal</div>
                </div>
              </div>

              {/* Macro split bar */}
              {totalGrams > 0 && (
                <div style={{ display: "flex", height: 6, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ width: `${pP}%`, background: "#E11D2E" }} />
                  <div style={{ width: `${pC}%`, background: "#EAB308" }} />
                  <div style={{ width: `${pF}%`, background: "#22D3EE" }} />
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                <MacroPill label="P" color="#E11D2E" value={d.protein_g} />
                <MacroPill label="C" color="#EAB308" value={d.carbs_g} />
                <MacroPill label="G" color="#22D3EE" value={d.fat_g} />
              </div>

              {d.notes && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#a3a3a3", paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                  {d.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {plan.notes && (
        <div className="qv-card-app">
          <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Notas de tu coach</div>
          <div style={{ fontSize: 13, color: "#cfcfcf", whiteSpace: "pre-wrap" }}>{plan.notes}</div>
        </div>
      )}
    </AppShell>
  );
}

function MacroPill({ label, color, value }) {
  return (
    <div style={{ background: "#060606", border: `1px solid ${color}33`, padding: "8px 10px" }}>
      <div style={{ fontSize: 10, color, letterSpacing: "0.2em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: "Bebas Neue", fontSize: "1.2rem", color: "#fff", lineHeight: 1, marginTop: 2 }}>
        {value ? `${value}g` : "—"}
      </div>
    </div>
  );
}
